import { useQuery } from '@tanstack/react-query';
import { PnLTimeSeriesDataPoint } from 'src/@types/performance';
import { getSpotMarketSymbol } from 'src/utils/DataApiClient';
import StatsApiClient from 'src/utils/StatsApiClient';
import { HistoricalTokenPriceDuration } from 'src/utils/graph';
import { ONE_DAY_MS } from '@velocity-exchange/common';
import { getApyFromApr } from 'src/utils/borrowLend';

export function useRateHistory({
	marketIndex,
	type,
	dateRange,
}: {
	marketIndex: number;
	type: 'borrow' | 'deposit';
	dateRange: HistoricalTokenPriceDuration;
}) {
	return useQuery<{ averageRate: number; chartData: PnLTimeSeriesDataPoint[] }>(
		{
			queryKey: ['rateHistory', marketIndex, type, dateRange],
			staleTime: ONE_DAY_MS,
			queryFn: async () => {
				const symbol = getSpotMarketSymbol(marketIndex);
				if (!symbol) {
					return { averageRate: 0, chartData: [] };
				}

				const result = await StatsApiClient.fetchRateHistory(symbol, type);

				if (result.success && result.rates) {
					// Filter data based on dateRange
					const daysToShow = parseInt(dateRange, 10);
					const cutoffTime = Date.now() - daysToShow * ONE_DAY_MS;

					let sum = 0;
					const points: PnLTimeSeriesDataPoint[] = [];

					for (const point of result.rates) {
						const timestamp = point[0] * 1000;
						if (timestamp >= cutoffTime) {
							const totalPnl = getApyFromApr(Number(point[1]) * 100, 'hourly');
							sum += totalPnl;
							points.push({
								date: new Date(timestamp),
								totalPnl,
							});
						}
					}

					return {
						averageRate: points.length > 0 ? sum / points.length : 0,
						chartData: points,
					};
				}
				return {
					averageRate: 0,
					chartData: [],
				};
			},
		}
	);
}
